import { Skeleton } from '@mui/material';
import classNames from 'classnames';
import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Map from 'src/components/ui/Map/Map';
import { DateFormats, emptyDate } from 'src/constants/dateFormats';
import { Ticket } from 'src/store/models/Ticket';
import { formatDate } from 'src/utils/dateFormatter';
import { formatListTicket } from 'src/utils/ticketUtils';

import ShadowBox from '../ShadowBox/ShadowBox';
import classes from './CustomerDetails.module.scss';

export interface CustomerDetailsProps {
  ticket: Ticket;
  loading?: boolean;
  className?: string;
}

const CustomerDetails: FC<CustomerDetailsProps> = ({
  ticket,
  loading = false,
  className,
}) => {
  const messageRef = useRef<HTMLParagraphElement>(null);
  const { t } = useTranslation();

  const [showButton, setShowButton] = useState(false);
  const [refVisible, setRefVisible] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const checkButton = () => {
      setShowButton(
        messageRef?.current?.scrollHeight !== messageRef?.current?.clientHeight
      );
      setOpen(false);
    };

    checkButton();
    window.addEventListener('resize', checkButton);
    return () => {
      window.removeEventListener('resize', checkButton);
    };
  }, [messageRef, ticket?.text]);

  useLayoutEffect(() => {
    if (refVisible) {
      setShowButton(
        messageRef?.current?.scrollHeight !== messageRef?.current?.clientHeight
      );
    }
  }, [refVisible]);

  return (
    <ShadowBox className={classNames(classes.Container, className)}>
      {!loading ? (
        <>
          <h5>{t('ticket_details:customer_details')}</h5>
          <p>
            {`${t('common:name')}: `}
            <span>{ticket?.user?.title}</span>
          </p>
          <p>
            {`${t('common:address')}: `}
            <span>{ticket?.user?.address}</span>
          </p>
          <p>
            {`${t('ticket_details:contact_details')}: `}
            <span>{`${ticket?.user?.eMail}, ${ticket?.user?.phone}`}</span>
          </p>
          <p>
            {`${t('common:device')}: `}
            <span>{ticket?.model || '/'}</span>
          </p>
          <p>
            {`${t('ticket_details:planned_maintenance')}: `}
            <span>
              {ticket?.checkUpDate
                ? formatDate(
                    ticket?.checkUpDate,
                    DateFormats.TICKET_LIST_FORMAT
                  )
                : emptyDate}
            </span>
          </p>
          <p
            className={classNames({
              [classes.Open]: open,
              [classes.ShowButton]: showButton,
            })}
            ref={(el) => {
              messageRef.current = el;
              setRefVisible(!!el);
            }}
          >
            {`${t('ticket_details:customer_message')}: `}
            <span>{ticket?.text || 'n/a'}</span>
          </p>
          {showButton && (
            <p className={classes.Show} onClick={() => setOpen(!open)}>
              {open
                ? t('ticket_details:show_less')
                : t('ticket_details:show_more')}
            </p>
          )}
          {!open && (
            <Map
              center={{
                lat: parseFloat(ticket?.user?.lat),
                lng: parseFloat(ticket?.user?.lon),
              }}
              className={classes.Map}
              markers={ticket ? [formatListTicket(ticket)] : []}
            />
          )}
          <p>{`${t('common:reported')}: ${formatDate(
            ticket?.createdAt,
            DateFormats.TICKET_LIST_FORMAT
          )}`}</p>
        </>
      ) : (
        <div className={classes.Loading}>
          <Skeleton animation="wave" variant="text" />
          <Skeleton animation="wave" variant="rectangular" height={205} />
          <Skeleton animation="wave" variant="rectangular" height={185} />
        </div>
      )}
    </ShadowBox>
  );
};

export default CustomerDetails;
